// Pure note/frequency helpers shared by the analysis worker, the Learn exercises, the audio lab and
// tests. No DOM access.

export const A4Midi = 69;
export const A4Hz = 440;

// Flat spellings match the note names used by the piano soundfont (see player/instrument.ts)
const flatNames = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
const sharpToFlat: { [name: string]: string } = { 'C#': 'Db', 'D#': 'Eb', 'F#': 'Gb', 'G#': 'Ab', 'A#': 'Bb' };

export function frequencyToMidi(hz: number): number {
  return A4Midi + 12 * Math.log2(hz / A4Hz);
}

export function midiToFrequency(midi: number): number {
  return A4Hz * Math.pow(2, (midi - A4Midi) / 12);
}

export function midiToNoteName(midi: number): string {
  const rounded = Math.round(midi);
  const octave = Math.floor(rounded / 12) - 1;
  return flatNames[((rounded % 12) + 12) % 12] + octave.toString();
}

export function noteNameToMidi(name: string): number | undefined {
  const match = name.trim().match(/^([A-Ga-g])([#b]?)(-?\d)$/);
  if (!match) {
    return undefined;
  }
  const letter = match[1].toUpperCase();
  const accidental = match[2];
  const octave = Number(match[3]);
  const spelled = accidental === '#' ? sharpToFlat[letter + '#'] : letter + accidental;
  const index = flatNames.indexOf(spelled);
  if (index < 0) {
    return undefined;
  }
  return (octave + 1) * 12 + index;
}

export function centsBetween(hz: number, referenceHz: number): number {
  return 1200 * Math.log2(hz / referenceHz);
}

// distance from the nearest equal-tempered semitone, in cents, plus that semitone's MIDI number
export function nearestSemitone(hz: number): { midi: number; cents: number } {
  const midi = frequencyToMidi(hz);
  const rounded = Math.round(midi);
  return { midi: rounded, cents: (midi - rounded) * 100 };
}

// fold a cents distance onto (-600, 600] so octave errors compare as "same pitch class"
export function foldToOctave(cents: number): number {
  let folded = cents % 1200;
  if (folded > 600) {
    folded -= 1200;
  } else if (folded <= -600) {
    folded += 1200;
  }
  return folded;
}

export function rms(samples: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) {
    sum += samples[i] * samples[i];
  }
  return Math.sqrt(sum / Math.max(1, samples.length));
}

export function toDbfs(value: number): number {
  return value <= 0 ? -Infinity : 20 * Math.log10(value);
}

export function median(values: number[]): number | undefined {
  if (values.length === 0) {
    return undefined;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}
